// Benchmarks editor: the SE's local case-study rows (`clay-bm-rows-v1`), keyed on use case ids.
// Search, per-use-case filter chips, sortable columns, inline cell edits, add / delete, and CSV
// paste / file import and export. Local to this browser: nothing here touches autosave or
// Salesforce. Rows of retired levers stay in storage, hidden, and never appear in the table.
//
// State: the rows live in the benchmark store (shared with the use case pages, so an edit shows
// up there at once); the panel keeps only view state (search, filter, sort, import box). Cells are
// bound straight to the store, so there is no draft to reseed when the page re-renders.

import { useMemo, useRef, useState } from "react";
import { AUTOFILL_IGNORE_PROPS } from "../../../_shared/autofill";
import { Modal } from "../../../_shared/components";
import { USE_CASES } from "../../data/use-case-catalog";
import {
  bmAuto,
  bmExportCSV,
  bmImportText,
  bmMetricOptions,
  bmSearch,
  bmSort,
  type BmSortCol,
} from "../../engine/benchmarks-v3";
import { visibleBenchmarkRows, type BmRow } from "../../engine/migrate-v3";
import {
  benchmarkActions,
  useBenchmarkStore,
} from "../../state/benchmark-store";
import { Ph } from "../Icon";

const COLUMNS: [BmSortCol, string][] = [
  ["customer", "Customer"],
  ["metric", "Use case"],
  ["pre", "Pre-Clay"],
  ["post", "Post-Clay"],
  ["imp", "Clay improvement"],
  ["notes", "Notes"],
];

function downloadCsv(rows: BmRow[]): void {
  const url = URL.createObjectURL(
    new Blob([bmExportCSV(rows)], { type: "text/csv" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = "clay-benchmarks.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
}

function BenchmarkRowEditor({
  row,
  options,
}: {
  row: BmRow;
  options: { v: string; label: string }[];
}) {
  const auto = bmAuto(row);
  const isAuto = !row.imp.trim() && !!auto;
  const known = options.some((o) => o.v === row.metricId);
  const patch = (p: Partial<Omit<BmRow, "id">>) =>
    benchmarkActions.patchRow(row.id, p);
  return (
    <div className="vc3-bm-row" role="row">
      <input
        {...AUTOFILL_IGNORE_PROPS}
        className="vc3-bm-cell vc3-bm-customer"
        aria-label="Customer"
        placeholder="Customer"
        spellCheck={false}
        value={row.customer}
        onChange={(e) => patch({ customer: e.target.value })}
      />
      <div className="vc3-bm-metric">
        <select
          className="vc3-bm-select"
          aria-label="Use case"
          value={row.metricId}
          onChange={(e) => patch({ metricId: e.target.value })}
        >
          {!known && (
            <option value={row.metricId}>
              {row.metricText || row.metricId}
            </option>
          )}
          {options.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
        {row.metricId === "other" && (
          <input
            {...AUTOFILL_IGNORE_PROPS}
            className="vc3-bm-cell"
            aria-label="Metric name"
            placeholder="Metric name"
            spellCheck={false}
            value={row.metricText}
            onChange={(e) => patch({ metricText: e.target.value })}
          />
        )}
      </div>
      <input
        {...AUTOFILL_IGNORE_PROPS}
        className="vc3-bm-cell"
        aria-label="Pre-Clay"
        placeholder="—"
        spellCheck={false}
        value={row.pre}
        onChange={(e) => patch({ pre: e.target.value })}
      />
      <input
        {...AUTOFILL_IGNORE_PROPS}
        className="vc3-bm-cell"
        aria-label="Post-Clay"
        placeholder="—"
        spellCheck={false}
        value={row.post}
        onChange={(e) => patch({ post: e.target.value })}
      />
      <div className="vc3-bm-imp">
        <input
          {...AUTOFILL_IGNORE_PROPS}
          className="vc3-bm-cell"
          aria-label="Clay improvement"
          placeholder={auto || "—"}
          spellCheck={false}
          value={row.imp}
          onChange={(e) => patch({ imp: e.target.value })}
        />
        {isAuto && <span className="vc3-bm-auto">auto</span>}
      </div>
      <textarea
        className="vc3-bm-cell vc3-bm-notes"
        aria-label="Notes"
        placeholder="Notes / context"
        spellCheck={false}
        rows={2}
        value={row.notes}
        onChange={(e) => patch({ notes: e.target.value })}
      />
      <button
        type="button"
        className="vc3-bm-del"
        title="Delete row"
        aria-label={`Delete the ${row.customer || "blank"} row`}
        onClick={() => benchmarkActions.deleteRow(row.id)}
      >
        <Ph name="trash" size={14} />
      </button>
    </div>
  );
}

function BenchmarksBody() {
  const allRows = useBenchmarkStore((s) => s.rows);
  const rows = useMemo(() => visibleBenchmarkRows(allRows), [allRows]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState<{ col: BmSortCol | ""; dir: 1 | -1 }>({
    col: "",
    dir: 1,
  });
  const [importOpen, setImportOpen] = useState(false);
  const [paste, setPaste] = useState("");
  const [note, setNote] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => bmMetricOptions(), []);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows) c[r.metricId] = (c[r.metricId] ?? 0) + 1;
    return c;
  }, [rows]);
  const shown = useMemo(
    () =>
      bmSort(
        bmSearch(
          filter === "all" ? rows : rows.filter((r) => r.metricId === filter),
          search,
        ),
        sort.col,
        sort.dir,
      ),
    [rows, filter, search, sort],
  );

  function applyImport(text: string) {
    const out = bmImportText(text, Date.now().toString(36));
    setNote(out.note);
    if (!out.ok) return;
    benchmarkActions.appendRows(out.rows);
    setPaste("");
  }

  async function importFile(file: File) {
    try {
      applyImport(await file.text());
    } catch {
      setNote("That file could not be read.");
    }
  }

  function cycleSort(col: BmSortCol) {
    setSort((s) =>
      s.col !== col
        ? { col, dir: 1 }
        : s.dir === 1
          ? { col, dir: -1 }
          : { col: "", dir: 1 },
    );
  }

  const chips: [string, string, number][] = [
    ["all", "All", rows.length],
    ...USE_CASES.filter((u) => counts[u.id]).map(
      (u): [string, string, number] => [u.id, u.name, counts[u.id]],
    ),
    ...Object.keys(counts)
      .filter((id) => id !== "other" && !USE_CASES.some((u) => u.id === id))
      .map((id): [string, string, number] => [id, id, counts[id]]),
    ...(counts.other
      ? ([["other", "Other", counts.other]] as [string, string, number][])
      : []),
  ];

  return (
    <div className="vc-root vc3-bm-shell">
      <div>
        <p className="vc3-help" style={{ margin: "0 0 12px" }}>
          Case-study results by use case: the proof behind each page. Edits stay
          in this browser; they are not saved to the account.
        </p>

        <div className="vc3-bm-toolbar">
          <label className="vc3-bm-search">
            <Ph name="magnifying-glass" size={14} />
            <input
              {...AUTOFILL_IGNORE_PROPS}
              aria-label="Search customers and notes"
              placeholder="Search customers & notes"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="vc3-btn vc3-btn-ghost"
            onClick={() => setImportOpen((o) => !o)}
            aria-expanded={importOpen}
          >
            <Ph name="upload-simple" size={14} /> Import CSV
          </button>
          <button
            type="button"
            className="vc3-btn vc3-btn-ghost"
            onClick={() => downloadCsv(shown)}
          >
            <Ph name="download-simple" size={14} /> Export CSV
          </button>
          <button
            type="button"
            className="vc3-btn"
            onClick={() =>
              benchmarkActions.addRow({
                id: "b" + Date.now().toString(36),
                customer: "",
                metricId: filter !== "all" ? filter : "other",
                metricText: "",
                pre: "",
                post: "",
                imp: "",
                notes: "",
              })
            }
          >
            <Ph name="plus" size={14} /> Add row
          </button>
        </div>

        <div
          className="vc3-bm-chips"
          role="group"
          aria-label="Filter by use case"
        >
          {chips.map(([id, label, n]) => (
            <button
              key={id}
              type="button"
              className="vc3-bm-chip"
              data-on={filter === id}
              aria-pressed={filter === id}
              onClick={() => setFilter(id)}
            >
              {label} <span>{n}</span>
            </button>
          ))}
        </div>

        {importOpen && (
          <div className="vc3-bm-import">
            <textarea
              className="vc3-input"
              aria-label="CSV or TSV rows to import"
              spellCheck={false}
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              placeholder="Paste CSV or TSV rows: customer, use case, pre-Clay, post-Clay, improvement, notes. A header row is detected; free-text metric names are matched to the closest use case (the original text is kept)."
            />
            <div className="vc3-bm-import-actions">
              <button
                type="button"
                className="vc3-btn"
                onClick={() => applyImport(paste)}
              >
                Add rows
              </button>
              <button
                type="button"
                className="vc3-btn vc3-btn-ghost"
                onClick={() => fileRef.current?.click()}
              >
                <Ph name="file-csv" size={14} /> Choose .csv file
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.tsv,.txt"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void importFile(file);
                }}
              />
              {note && (
                <span className="vc3-help" role="status">
                  {note}
                </span>
              )}
            </div>
          </div>
        )}

        <div
          className="vc3-bm-table"
          role="table"
          aria-label="Customer benchmarks"
        >
          <div className="vc3-bm-row vc3-bm-header" role="row">
            {COLUMNS.map(([col, label]) => (
              <button
                key={col}
                type="button"
                role="columnheader"
                className="vc3-bm-th"
                onClick={() => cycleSort(col)}
              >
                {label}
                <Ph
                  name={
                    sort.col === col
                      ? sort.dir === 1
                        ? "caret-up"
                        : "caret-down"
                      : "caret-up-down"
                  }
                  size={11}
                />
              </button>
            ))}
            <span />
          </div>
          <div className="vc3-bm-body">
            {shown.map((r) => (
              <BenchmarkRowEditor key={r.id} row={r} options={options} />
            ))}
            {!shown.length && (
              <p className="vc3-help vc3-bm-empty">No benchmarks match.</p>
            )}
          </div>
        </div>
        <div className="vc3-bm-foot vc3-help">
          {shown.length} of {rows.length} benchmark
          {rows.length === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}
/** The top bar's entry point: a link that opens the editor. */
export function BenchmarksButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="vc3-linkbtn"
        onClick={() => setOpen(true)}
      >
        <Ph name="table" size={13} /> Benchmarks
      </button>
      <Modal
        open={open}
        title="Customer benchmarks"
        onClose={() => setOpen(false)}
        containerClassName="md:max-w-[min(100vw-96px,80rem)]"
      >
        <BenchmarksBody />
      </Modal>
    </>
  );
}
